import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { ArrowUpCircle, ArrowDownCircle, Check, X, Clock, Eye, ExternalLink } from 'lucide-react'
import type { UpgradeRequest } from '@/types/admin'

// ── Request type badge ─────────────────────────────────────────────────────────

function RequestTypeBadge({ req }: { req: UpgradeRequest }) {
  const currentOrder = req.current_plan?.sort_order ?? -1
  const requestedOrder = req.plans?.sort_order ?? 0

  if (currentOrder < 0 || currentOrder === requestedOrder) {
    // No prior plan or same plan (trial conversion / renewal)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
        <ArrowUpCircle className="h-3 w-3" />
        Beli
      </span>
    )
  }
  if (requestedOrder > currentOrder) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        <ArrowUpCircle className="h-3 w-3" />
        Upgrade
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
      <ArrowDownCircle className="h-3 w-3" />
      Downgrade
    </span>
  )
}

type FilterStatus = 'active' | 'pending' | 'proof_uploaded' | 'approved' | 'rejected' | 'all'

// ── Status badge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: UpgradeRequest['status'] }) {
  const map: Record<UpgradeRequest['status'], { label: string; className: string }> = {
    pending: { label: 'Menunggu', className: 'bg-yellow-100 text-yellow-700' },
    proof_uploaded: { label: 'Bukti Terkirim', className: 'bg-blue-100 text-blue-700' },
    approved: { label: 'Disetujui', className: 'bg-green-100 text-green-700' },
    rejected: { label: 'Ditolak', className: 'bg-red-100 text-red-700' },
  }
  const { label, className } = map[status]
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      {label}
    </span>
  )
}

// ── Proof preview modal ────────────────────────────────────────────────────────
// Fetches a signed URL from the Edge API (service role) because the
// subscription-proofs bucket is private.

function ProofModal({
  request,
  onClose,
}: {
  request: UpgradeRequest
  onClose: () => void
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'upgrade-proof-url', request.id],
    queryFn: () => adminApi.getUpgradeRequestProofUrl(request.id),
    enabled: !!request.proof_url,
    staleTime: 1000 * 50, // signed URL valid 1 hour; refetch after 50 min
    retry: 1,
  })

  const signedUrl = data?.signed_url
  const isPdf = request.proof_url?.toLowerCase().includes('.pdf')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-xl border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h3 className="font-semibold text-sm">Bukti Pembayaran</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {request.tenants?.name ?? 'Tenant'} → {request.plans?.name ?? '?'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 min-h-[120px] flex flex-col justify-center">
          {!request.proof_url ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Tidak ada bukti yang diunggah.
            </p>
          ) : isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              Memuat bukti...
            </div>
          ) : error || !signedUrl ? (
            <p className="py-6 text-center text-sm text-destructive">
              Gagal memuat bukti. Coba lagi.
            </p>
          ) : isPdf ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <p className="text-sm text-muted-foreground">Bukti dalam format PDF.</p>
              <a
                href={signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Buka PDF
              </a>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <img
                src={signedUrl}
                alt="Bukti pembayaran"
                className="max-h-96 w-full rounded-lg object-contain border"
              />
              <a
                href={signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-xs hover:bg-muted"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Buka di tab baru
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function UpgradeRequestsPage() {
  const qc = useQueryClient()
  const [filter, setFilter] = useState<FilterStatus>('active')
  const [viewRequest, setViewRequest] = useState<UpgradeRequest | null>(null)
  const [rejectTarget, setRejectTarget] = useState<UpgradeRequest | null>(null)
  const [rejectNote, setRejectNote] = useState('')

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['admin', 'upgrade-requests', filter],
    queryFn: () => adminApi.getUpgradeRequests(filter),
    staleTime: 1000 * 30,
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => adminApi.approveUpgradeRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'upgrade-requests'] })
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, admin_note }: { id: string; admin_note?: string }) =>
      adminApi.rejectUpgradeRequest(id, admin_note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'upgrade-requests'] })
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] })
      setRejectTarget(null)
      setRejectNote('')
    },
  })

  const filterLabels: Record<FilterStatus, string> = {
    active: 'Perlu Ditinjau',
    pending: 'Menunggu',
    proof_uploaded: 'Bukti Terkirim',
    approved: 'Disetujui',
    rejected: 'Ditolak',
    all: 'Semua',
  }

  const pendingCount = requests.filter(
    (r) => r.status === 'pending' || r.status === 'proof_uploaded'
  ).length

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Upgrade Langganan</h1>
        <p className="text-sm text-muted-foreground">
          Permintaan upgrade plan dari tenant.
          {filter === 'active' && pendingCount > 0 && (
            <span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
              {pendingCount} perlu ditinjau
            </span>
          )}
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex w-fit flex-wrap gap-1 rounded-lg border bg-muted/50 p-1">
        {(['active', 'approved', 'rejected', 'all'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
              filter === s
                ? 'bg-background font-medium shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {filterLabels[s]}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        {isLoading ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <ArrowUpCircle className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {filter === 'active'
                ? 'Tidak ada permintaan yang perlu ditinjau.'
                : `Tidak ada permintaan dengan status "${filterLabels[filter]}".`}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Tenant</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Jenis</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Plan</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Bukti Bayar</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Tanggal</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <p className="font-medium">{req.tenants?.name ?? '—'}</p>
                    {req.tenants?.slug && (
                      <p className="text-xs font-mono text-muted-foreground">
                        {req.tenants.slug}.oneidol.id
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <RequestTypeBadge req={req} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-sm">
                      <span className="text-muted-foreground">
                        {req.current_plan?.name ?? <span className="italic">—</span>}
                      </span>
                      <span className="text-muted-foreground">→</span>
                      <span className="font-medium">{req.plans?.name ?? '—'}</span>
                    </div>
                    {req.plans?.price_idr != null && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {new Intl.NumberFormat('id-ID', {
                          style: 'currency',
                          currency: 'IDR',
                          maximumFractionDigits: 0,
                        }).format(req.plans.price_idr)}
                        /bln
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={req.status} />
                    {req.admin_note && (
                      <p
                        className="mt-0.5 max-w-[160px] truncate text-xs italic text-muted-foreground"
                        title={req.admin_note}
                      >
                        {req.admin_note}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {req.proof_url ? (
                      <button
                        onClick={() => setViewRequest(req)}
                        className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted"
                      >
                        <Eye className="h-3 w-3" />
                        Lihat Bukti
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">Belum ada</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatDate(req.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">

                      {/* Approve / Reject — only for actionable statuses */}
                      {(req.status === 'pending' || req.status === 'proof_uploaded') && (
                        <>
                          <button
                            onClick={() => approveMutation.mutate(req.id)}
                            disabled={approveMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            <Check className="h-3 w-3" />
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              setRejectTarget(req)
                              setRejectNote('')
                            }}
                            disabled={rejectMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted disabled:opacity-50"
                          >
                            <X className="h-3 w-3" />
                            Tolak
                          </button>
                        </>
                      )}

                      {req.status === 'approved' && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600">
                          <Check className="h-3 w-3" />
                          Disetujui
                          {req.verified_at && (
                            <span className="text-muted-foreground">
                              {' '}· {formatDate(req.verified_at)}
                            </span>
                          )}
                        </span>
                      )}

                      {req.status === 'rejected' && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          Menunggu re-upload
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Menampilkan {requests.length} permintaan
      </p>

      {/* Proof preview modal */}
      {viewRequest && (
        <ProofModal request={viewRequest} onClose={() => setViewRequest(null)} />
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-xl border bg-card p-5 shadow-xl">
            <h3 className="mb-1 font-semibold">Tolak Permintaan</h3>
            <p className="mb-1 text-sm text-muted-foreground">
              Tenant: <strong className="text-foreground">{rejectTarget.tenants?.name}</strong> →{' '}
              {rejectTarget.plans?.name}
            </p>
            <p className="mb-3 text-sm text-muted-foreground">
              Sertakan alasan penolakan — akan ditampilkan ke tenant (opsional).
            </p>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="Contoh: bukti tidak terbaca, nominal tidak sesuai..."
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="mt-3 flex gap-2">
              <button
                onClick={() =>
                  rejectMutation.mutate({
                    id: rejectTarget.id,
                    admin_note: rejectNote || undefined,
                  })
                }
                disabled={rejectMutation.isPending}
                className="flex-1 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Menolak...' : 'Konfirmasi Tolak'}
              </button>
              <button
                onClick={() => {
                  setRejectTarget(null)
                  setRejectNote('')
                }}
                className="rounded-md border px-3 py-2 text-sm hover:bg-muted"
              >
                Batal
              </button>
            </div>
            {rejectMutation.error && (
              <p className="mt-2 text-xs text-red-600">
                {(rejectMutation.error as Error).message}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
